// Configuration panel. Writes changes directly to the store.
// Structure is built once; subscribe() only updates values, not DOM topology,
// so input focus is preserved while the user edits.

import type { Store } from '../store';
import type { FinderConfig, HolidayCountry } from '../types';
import { WEEKDAY_LABELS_DE } from '../time';
import { createTimeRangePicker } from './time-range-picker';
import { openDateRangePicker } from './date-range-picker';
import { el } from './dom';

const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0]; // Mo..So

const GRANULARITIES: Array<FinderConfig['slotSizeMinutes']> = [15, 30, 60];

function formatIsoForButton(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return date.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
}

function oneMonthAfter(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setMonth(dt.getMonth() + 1);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

const COUNTRY_OPTIONS: Array<{ value: HolidayCountry; label: string; flag: string }> = [
  { value: 'DE', label: 'Deutschland', flag: '🇩🇪' },
  { value: 'AT', label: 'Österreich',  flag: '🇦🇹' },
  { value: 'CH', label: 'Schweiz',     flag: '🇨🇭' },
];

export function renderConfigPanel(store: Store): HTMLElement {
  const root = el('section', { class: 'config-panel', 'aria-label': 'Konfiguration' });

  // Track whether the user has manually adjusted "Bis" within this session.
  // If they haven't, changing "Von" auto-snaps "Bis" to one month later;
  // once they've set "Bis" themselves, we never overwrite it.
  let endTouched = false;

  // Custom 3-Monats-Datums-Picker als Popover. Knopf statt Native-Date-
  // Input — auf Mobile wesentlich einfacher zu bedienen, plus konsistente
  // Optik mit dem Time-Picker.
  const rangeStartInput = el('button',
    { type: 'button', class: 'input-date input-date-button', 'aria-label': 'Von-Datum auswählen' },
    '—',
  ) as HTMLButtonElement;
  rangeStartInput.addEventListener('click', () => {
    openDateRangePicker({
      value: store.get().config.rangeStart,
      anchor: rangeStartInput,
      onPick: (iso) => {
        const patch: Partial<FinderConfig> = { rangeStart: iso };
        if (!endTouched) patch.rangeEnd = oneMonthAfter(iso);
        store.patchConfig(patch);
      },
    });
  });

  const rangeEndInput = el('button',
    { type: 'button', class: 'input-date input-date-button', 'aria-label': 'Bis-Datum auswählen' },
    '—',
  ) as HTMLButtonElement;
  rangeEndInput.addEventListener('click', () => {
    openDateRangePicker({
      value: store.get().config.rangeEnd,
      anchor: rangeEndInput,
      onPick: (iso) => {
        endTouched = true;
        store.patchConfig({ rangeEnd: iso });
      },
    });
  });

  const initialConfig = store.get().config;
  const timeRange = createTimeRangePicker({
    dayStart: initialConfig.dayStartMinutes,
    dayEnd:   initialConfig.dayEndMinutes,
    onChange: (start, end) => {
      store.patchConfig({ dayStartMinutes: start, dayEndMinutes: end });
    },
  });

  // Weekday picker
  const weekdayButtons = new Map<number, HTMLButtonElement>();
  const weekdayRow = el('div', { class: 'weekday-picker' });
  for (const day of WEEKDAY_ORDER) {
    const isWeekendDay = day === 0 || day === 6;
    const btn = el('button',
      { type: 'button', class: `weekday-btn${isWeekendDay ? ' is-weekend' : ''}` },
      WEEKDAY_LABELS_DE[day],
    ) as HTMLButtonElement;
    btn.addEventListener('click', () => {
      const current = new Set(store.get().config.allowedWeekdays);
      if (current.has(day)) current.delete(day);
      else current.add(day);
      store.patchConfig({ allowedWeekdays: [...current].sort() });
    });
    weekdayButtons.set(day, btn);
    weekdayRow.append(btn);
  }

  const presets = el('div', { class: 'weekday-presets' });
  const presetData: Array<[string, number[], boolean]> = [
    ['Werktage', [1, 2, 3, 4, 5], false],
    ['Wochenende', [0, 6], true],
    ['Alle', [0, 1, 2, 3, 4, 5, 6], false],
  ];
  for (const [label, days, isWeekendPreset] of presetData) {
    const btn = el('button',
      { type: 'button', class: `preset-btn${isWeekendPreset ? ' is-weekend' : ''}` },
      label,
    );
    btn.addEventListener('click', () => store.patchConfig({ allowedWeekdays: days }));
    presets.append(btn);
  }

  // Granularity picker
  const granularityButtons = new Map<number, HTMLButtonElement>();
  const granularityRow = el('div', { class: 'granularity-picker' });
  for (const g of GRANULARITIES) {
    const btn = el('button', { type: 'button', class: 'granularity-btn' }, `${g} min`) as HTMLButtonElement;
    btn.addEventListener('click', () => {
      const cur = store.get().config;
      const snappedDuration = Math.max(g, Math.round(cur.defaultDurationMinutes / g) * g);
      store.patchConfig({ slotSizeMinutes: g, defaultDurationMinutes: snappedDuration });
    });
    granularityButtons.set(g, btn);
    granularityRow.append(btn);
  }

  // Duration select — options are rebuilt when raster changes.
  const durationSelect = el('select', { class: 'input-duration' }) as HTMLSelectElement;
  durationSelect.addEventListener('change', () => {
    const n = Number(durationSelect.value);
    if (!Number.isNaN(n)) store.patchConfig({ defaultDurationMinutes: n });
  });

  // Country picker — Klick aufs Wappen toggelt das Land. Keine Checkbox,
  // kein Auto-Button (würde externe Anfrage + Dialog erfordern).
  const countryButtons = new Map<HolidayCountry, HTMLButtonElement>();
  const countryRow = el('div', { class: 'country-row' });
  for (const opt of COUNTRY_OPTIONS) {
    const btn = el('button', {
      type: 'button',
      class: 'country-btn',
      title: opt.label,
      'aria-label': opt.label,
      'aria-pressed': 'false',
    }, opt.flag) as HTMLButtonElement;
    btn.addEventListener('click', () => {
      const cur = new Set(store.get().config.holidayCountries);
      if (cur.has(opt.value)) cur.delete(opt.value);
      else cur.add(opt.value);
      store.patchConfig({ holidayCountries: [...cur].sort() as HolidayCountry[] });
    });
    countryButtons.set(opt.value, btn);
    countryRow.append(btn);
  }

  // Assemble layout. Tageszeit-Picker bekommt eine eigene Zeile direkt unter
  // dem Datumsbereich — er ist optisch dominant (Strang + zwei Preset-Reihen)
  // und sollte nicht in eine geteilte Zeile mit kleinen Inputs gequetscht
  // werden.
  root.append(
    el(
      'div',
      { class: 'config-row' },
      labelled('Von', rangeStartInput),
      labelled('Bis', rangeEndInput),
    ),
    el(
      'div',
      { class: 'config-row config-row-wide' },
      labelled('Tageszeit', timeRange.root),
    ),
    el(
      'div',
      { class: 'config-row' },
      labelled('Wochentage', el('div', { class: 'weekday-wrap' }, weekdayRow, presets)),
    ),
    el(
      'div',
      { class: 'config-row' },
      labelled('Raster', granularityRow),
      labelled('Dauer', durationSelect),
      labelled('Feiertage', countryRow),
    ),
  );

  // Subscribe: update values without rebuilding structure.
  store.subscribe((s) => {
    const c = s.config;

    rangeStartInput.textContent = formatIsoForButton(c.rangeStart);
    rangeEndInput.textContent   = formatIsoForButton(c.rangeEnd);

    timeRange.update(c.dayStartMinutes, c.dayEndMinutes);

    const activeDays = new Set(c.allowedWeekdays);
    for (const [day, btn] of weekdayButtons) {
      btn.classList.toggle('is-active', activeDays.has(day));
    }

    for (const [g, btn] of granularityButtons) {
      btn.classList.toggle('is-active', g === c.slotSizeMinutes);
    }

    // Rebuild duration options if raster changed — avoid rebuild if options match.
    const wantedOptions: number[] = [];
    for (let d = c.slotSizeMinutes; d <= 180; d += c.slotSizeMinutes) wantedOptions.push(d);
    const currentOptions = Array.from(durationSelect.options).map((o) => Number(o.value));
    const optionsMatch =
      currentOptions.length === wantedOptions.length && currentOptions.every((v, i) => v === wantedOptions[i]);
    if (!optionsMatch) {
      durationSelect.replaceChildren();
      for (const d of wantedOptions) {
        const opt = el('option', { value: d }, `${d} min`);
        durationSelect.append(opt);
      }
    }
    if (durationSelect.value !== String(c.defaultDurationMinutes)) {
      durationSelect.value = String(c.defaultDurationMinutes);
    }

    const activeCountries = new Set(c.holidayCountries);
    for (const [code, btn] of countryButtons) {
      const on = activeCountries.has(code);
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', String(on));
    }
  });

  return root;
}

function labelled(label: string, control: HTMLElement): HTMLElement {
  return el('label', { class: 'config-field' }, el('span', { class: 'config-label' }, label), control);
}
